import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { expenseService } from '../../services/expenseService';
import { formatCurrency, formatDate, formatDateTime } from '../../utils/formatters';

const statusStyle: Record<string, React.CSSProperties> = {
  COMPLETED: { background: 'rgba(90,201,140,0.12)', color: '#5ac98c', border: '1px solid rgba(90,201,140,0.25)' },
  PENDING:   { background: 'rgba(92,78,51,0.22)', color: '#d97706', border: '1px solid rgba(92,78,51,0.35)' },
  FAILED:    { background: 'rgba(201,90,90,0.12)', color: '#c95a5a', border: '1px solid rgba(201,90,90,0.25)' },
  CANCELLED: { background: 'rgba(80,80,80,0.15)', color: '#555570', border: '1px solid rgba(80,80,80,0.2)' },
};

const thStyle: React.CSSProperties = {
  padding: '10px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  fontWeight: 500,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  borderBottom: '1px solid rgba(92,78,51,0.15)',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  borderBottom: '1px solid rgba(241,245,249,1)',
  verticalAlign: 'middle',
};

export const ExpensesList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const { data: expensesData, isLoading, error } = useQuery({
    queryKey: ['expenses', currentPage, searchTerm],
    queryFn: () => expenseService.getExpenses({
      page: currentPage,
      limit: pageSize,
      search: searchTerm || undefined,
    }),
  });

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const expenses = expensesData?.expenses || [];
  const pagination = expensesData?.pagination;

  if (isLoading) {
    return (
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: 48, textAlign: 'center' }}>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c' }}>Loading expenses...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: '#ffffff', border: '1px solid rgba(201,90,90,0.2)', borderRadius: 6, padding: 24, textAlign: 'center' }}>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#c95a5a' }}>Error loading expenses. Please try again.</p>
      </div>
    );
  }

  return (
    <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6 }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 300, color: '#1c2033' }}>Expense History</div>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>Direct expenses recorded outside the bill workflow</div>
        </div>
        <div style={{ position: 'relative' }}>
          <svg width="14" height="14" fill="none" stroke="rgba(217,119,6,0.35)" viewBox="0 0 24 24" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search expenses..."
            value={searchTerm}
            onChange={handleSearchChange}
            style={{
              paddingLeft: 32,
              paddingRight: 12,
              paddingTop: 8,
              paddingBottom: 8,
              background: '#f4efe2',
              border: '1px solid rgba(92,78,51,0.25)',
              borderRadius: 4,
              fontFamily: "'Inter', sans-serif",
              fontSize: 12,
              color: '#5b5a52',
              outline: 'none',
              width: 220,
            }}
            onFocus={e => { (e.target as HTMLInputElement).style.borderColor = 'rgba(217,119,6,0.35)'; }}
            onBlur={e => { (e.target as HTMLInputElement).style.borderColor = 'rgba(92,78,51,0.25)'; }}
          />
        </div>
      </div>

      {expenses.length > 0 ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(201,165,90,0.02)' }}>
                <th style={thStyle}>Expense #</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Expense Account</th>
                <th style={thStyle}>Paid From</th>
                <th style={thStyle}>Vendor / Payee</th>
                <th style={thStyle}>Method</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((expense) => (
                <tr
                  key={expense.id}
                  style={{ transition: 'background 0.15s' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(248,250,252,1)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <td style={tdStyle}>
                    <div style={{ color: '#d97706' }}>{expense.expenseNumber}</div>
                    {expense.reference && (
                      <div style={{ fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>Ref: {expense.reference}</div>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <div>{formatDate(expense.date)}</div>
                    <div style={{ fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>{formatDateTime(expense.createdAt)}</div>
                  </td>
                  <td style={tdStyle}>
                    {expense.expenseAccount ? `${expense.expenseAccount.code} - ${expense.expenseAccount.name}` : 'N/A'}
                  </td>
                  <td style={tdStyle}>
                    {expense.bankAccount ? `${expense.bankAccount.accountName} - ${expense.bankAccount.bankName}` : (expense.paymentAccount ? `${expense.paymentAccount.code} - ${expense.paymentAccount.name}` : 'N/A')}
                  </td>
                  <td style={tdStyle}>
                    {expense.vendor?.name || expense.payeeName || '—'}
                  </td>
                  <td style={tdStyle}>
                    {expenseService.formatPaymentMethod(expense.paymentMethod)}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, color: '#fbbf24' }}>
                      {formatCurrency(expense.amount)}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <span style={{
                      ...(statusStyle[expense.status] || statusStyle.COMPLETED),
                      display: 'inline-block',
                      padding: '3px 10px',
                      borderRadius: 3,
                      fontFamily: "'Inter', sans-serif",
                      fontSize: 8,
                      letterSpacing: '0.15em',
                      textTransform: 'uppercase',
                    }}>
                      {expense.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <svg width="40" height="40" fill="none" stroke="rgba(217,119,6,0.25)" viewBox="0 0 24 24" style={{ margin: '0 auto 12px' }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, letterSpacing: '0.2em', color: '#6b6a5c' }}>
            {searchTerm ? 'NO RESULTS FOUND' : 'NO EXPENSES YET'}
          </p>
          {searchTerm && (
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#8a8778', marginTop: 6 }}>
              Try adjusting your search terms.
            </p>
          )}
        </div>
      )}

      {pagination && pagination.totalPages > 1 && (
        <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(92,78,51,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c' }}>
            Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1}–{Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of {pagination.totalItems} expenses
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage <= 1}
              style={{
                padding: '4px 12px',
                fontFamily: "'Inter', sans-serif",
                fontSize: 9,
                letterSpacing: '0.1em',
                background: 'rgba(239,232,213,0.5)',
                color: currentPage <= 1 ? '#c9c2ab' : '#8a8778',
                border: '1px solid rgba(92,78,51,0.22)',
                borderRadius: 3,
                cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
              }}
            >
              Previous
            </button>
            <span style={{ padding: '4px 12px', fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c' }}>
              {pagination.currentPage} / {pagination.totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
              disabled={currentPage >= pagination.totalPages}
              style={{
                padding: '4px 12px',
                fontFamily: "'Inter', sans-serif",
                fontSize: 9,
                letterSpacing: '0.1em',
                background: 'rgba(239,232,213,0.5)',
                color: currentPage >= pagination.totalPages ? '#c9c2ab' : '#8a8778',
                border: '1px solid rgba(92,78,51,0.22)',
                borderRadius: 3,
                cursor: currentPage >= pagination.totalPages ? 'not-allowed' : 'pointer',
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
